import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import CountUp from 'react-countup';
import { useInView } from 'react-intersection-observer';
import { FaBox, FaTruck, FaUsers, FaAward } from 'react-icons/fa';
import useScrollAnimation from '../hooks/useScrollAnimation';

const Stats = () => {
  const { t, i18n } = useTranslation();
  const [stats, setStats] = useState(null);
  const [animationStarted, setAnimationStarted] = useState(false);
  const { ref, inView } = useInView({ threshold: 0.1 });
  const statsRef = useScrollAnimation(0.2);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch('https://hub.mileageriot.com/api/basic-stats');
        if (!response.ok) throw new Error(`Stats request failed: ${response.status}`);

        const data = await response.json();
        setStats(data.data);
      } catch (error) {
        console.error('Error fetching stats item data:', error);
      }
    };

    fetchStats();
  }, []);

  const numberSeparator = i18n.resolvedLanguage?.startsWith('ru') ? ' ' : ',';

  const metrics = [
    {
      key: 'distance',
      index: '01',
      icon: FaTruck,
      value: stats?.distance,
      unit: 'KM',
      title: t('stats.distance.title'),
      subtitle: t('stats.distance.subtitle'),
    },
    {
      key: 'jobs',
      index: '02',
      icon: FaBox,
      value: stats?.jobs,
      title: t('stats.jobs.title'),
      subtitle: t('stats.jobs.subtitle'),
    },
    {
      key: 'users',
      index: '03',
      icon: FaUsers,
      value: stats?.users,
      title: t('stats.users.title'),
      subtitle: t('stats.users.subtitle'),
    },
  ];

  return (
    <section className="stats-modern stats-upgraded" ref={statsRef}>
      <div className="stats-ambient stats-ambient-left" aria-hidden="true"></div>
      <div className="stats-ambient stats-ambient-right" aria-hidden="true"></div>

      <div className="container stats-container">
        <div className="section-header stats-section-header text-center fade-in-up">
          <div className="section-badge">
            <FaAward className="me-2" />
            <span>{t('stats.title') || 'Our Statistics'}</span>
          </div>
          <h2 className="section-title text-white">
            {t('stats.mainTitle') || 'Achievement Numbers'}
          </h2>
          <div className="section-decoration"></div>
          <p className="stats-section-subtitle">
            {t('stats.subtitle') || 'Mileage Riot in numbers — updated automatically'}
          </p>
        </div>

        <div className="stats-grid-upgraded" ref={ref} aria-live="polite">
          {metrics.map((metric) => {
            const Icon = metric.icon;

            return (
              <article className={`stats-card-upgraded stats-card-${metric.key} stagger-child`} key={metric.key}>
                <div className="stats-card-top">
                  <span className="stats-icon-tile" aria-hidden="true">
                    <Icon />
                  </span>
                  <span className="stats-card-index">{metric.index}</span>
                </div>

                <div className="stats-metric">
                  {metric.value !== undefined && metric.value !== null ? (
                    animationStarted || inView ? (
                      <CountUp
                        start={0}
                        end={metric.value}
                        duration={2.5}
                        decimals={0}
                        separator={numberSeparator}
                        onEnd={() => setAnimationStarted(true)}
                      />
                    ) : 0
                  ) : (
                    <span className="stats-loading">{t('stats.loading') || 'Loading...'}</span>
                  )}
                  {metric.unit && <span className="stats-unit-upgraded">{metric.unit}</span>}
                </div>

                <div className="stats-card-label">
                  <span>{metric.title}</span>
                  <small>{metric.subtitle}</small>
                </div>

                <div className="stats-card-footer" aria-hidden="true">
                  <span className="stats-live-dot"></span>
                  <span className="stats-footer-line"></span>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Stats;
